"use client";
import { useRovingTabs } from "@/hooks/useRovingTabs.hook";

import RichText from "@/components/common/richText/richText.component";
import { cn } from "@/lib/utils";
import { useId } from "react";
import type { PaymentFlow } from "../../data";
import { FlowScreen } from "./components";

interface PaymentFlowsProps {
  ariaLabel: string;
  tablistLabel: string;
  flows: PaymentFlow[];
}

// The preview's `payment_flows` visual: one pill tab per way of paying,
// each showing three numbered steps beside a phone (or diagram) and a
// "Best for" note.
const PaymentFlows = ({
  ariaLabel,
  tablistLabel,
  flows,
}: PaymentFlowsProps) => {
  const {
    activeIndex: active,
    select,
    onTabKeyDown: onKeyDown,
  } = useRovingTabs(flows.length);
  const baseId = useId();

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0 grid gap-[18px]">
      <div
        role="tablist"
        aria-label={tablistLabel}
        className="flex gap-2 overflow-x-auto px-0.5 pt-0.5 pb-2 [scrollbar-width:none] lg:justify-center"
      >
        {flows.map((flow, index) => {
          const isActive = index === active;
          return (
            <button
              key={flow.tab}
              type="button"
              role="tab"
              id={`${baseId}-tab-${index}`}
              aria-controls={`${baseId}-panel-${index}`}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                "shrink-0 cursor-pointer rounded-full border-[1.5px] px-[18px] py-2.5 text-[15px] font-bold whitespace-nowrap transition-colors duration-200 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none",
                isActive
                  ? "border-primary bg-primary text-white"
                  : "border-line bg-white text-ink hover:border-line-warm",
              )}
            >
              {flow.tab}
            </button>
          );
        })}
      </div>
      {flows.map((flow, index) => (
        <div
          key={flow.tab}
          role="tabpanel"
          id={`${baseId}-panel-${index}`}
          aria-labelledby={`${baseId}-tab-${index}`}
          hidden={index !== active}
          className="mx-auto w-full max-w-[900px]"
        >
          <div className="grid items-center gap-6 md:grid-cols-[1.2fr_0.8fr]">
            <ol className="grid gap-3.5">
              {flow.steps.map((step, stepIndex) => (
                <li
                  key={step.title}
                  className="grid grid-cols-[36px_1fr] items-start gap-3"
                >
                  <span
                    aria-hidden="true"
                    className="grid size-9 place-items-center rounded-full bg-primary font-extrabold text-white"
                  >
                    {stepIndex + 1}
                  </span>
                  <div>
                    <b className="block text-base">{step.title}</b>
                    <small className="text-sm text-ink-muted">
                      {step.text}
                    </small>
                  </div>
                </li>
              ))}
            </ol>
            <FlowScreen screen={flow.screen} />
          </div>
          <p className="mt-4 rounded-[14px] bg-white px-4 py-2.5 text-[15px]">
            <RichText text={flow.best} strongClassName="font-bold" />
          </p>
        </div>
      ))}
    </figure>
  );
};

export default PaymentFlows;
